/**
 * #742 Command records the current root AGENTS.md through `record_owner_contract`.
 *
 * The database stays the authority: owner AAL2, exact request keys, and a hash
 * that is the SHA-256 of the UTF-8 text. This module sends that text only inside
 * the RPC request. The status it returns is an identity or a fixed refusal.
 * It does not write an audit row and it does not keep the text.
 */
import { isSecretShapedInstruction, parseRecordedOwnerContract } from '@brunel/shared';

export const OWNER_CONTRACT_FILE_NAME = 'AGENTS.md';
export const OWNER_CONTRACT_MAX_CHARS = 200_000;

/** Unicode characters, matching Postgres `char_length` for this text. */
function charLength(text: string): number {
  return [...text].length;
}

/** SHA-256 hex of the UTF-8 text, matching `encode(sha256(convert_to(text, 'UTF8')), 'hex')`. */
export async function ownerContractContentSha256(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export type OwnerContractFileRead =
  | { readonly ok: true; readonly text: string; readonly hash: string }
  | { readonly ok: false; readonly message: string };

/**
 * Accept only a file named AGENTS.md. Decode UTF-8 (a leading BOM is not part
 * of the text) and refuse empty, over-long, or secret-shaped text before any RPC.
 */
export async function readOwnerContractFile(file: File): Promise<OwnerContractFileRead> {
  if (file.name !== OWNER_CONTRACT_FILE_NAME) return { ok: false, message: 'Choose the file named AGENTS.md.' };
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer());
  } catch {
    return { ok: false, message: 'That file is not UTF-8 text.' };
  }
  if (text.length === 0 || text.trim() === '') return { ok: false, message: 'That file is empty.' };
  if (charLength(text) > OWNER_CONTRACT_MAX_CHARS) return { ok: false, message: 'That file is too long to record.' };
  if (isSecretShapedInstruction(text)) return { ok: false, message: 'Brunel refused the file because it looks like a credential.' };
  return { ok: true, text, hash: await ownerContractContentSha256(text) };
}

/** The owner-facing sentence for a refusal from `record_owner_contract`. Never includes the text. */
export function recordOwnerContractMessage(code: string): string {
  switch (code) {
    case 'forbidden': return 'Only the owner can record the owner contract, after confirming it is you.';
    case 'unauthorized': return 'Your session has expired. Sign in again to record the owner contract.';
    case 'invalid_input': return 'Brunel did not accept that file as the current owner contract.';
    case 'not_found': return 'Brunel has no organization for that record.';
    case 'secret_shaped': return 'Brunel refused the file because it looks like a credential.';
    default: return 'The owner contract was not recorded.';
  }
}

export interface RecordOwnerContractResult {
  readonly ok: boolean;
  readonly message: string;
}

type RpcAnswer = { ok?: boolean; error?: { code?: string }; value?: unknown } | null;

/**
 * Send the file once, behind the owner's AAL2 guard. The guard runs first: if it
 * never calls the action, the RPC is not called. A success is an identity. An
 * extra key, including the text, is "did not return an identity".
 */
export async function recordOwnerContract(input: {
  readonly orgId: string;
  readonly text: string;
  readonly hash: string;
  readonly guard: (name: 'post_owner_command', action: () => Promise<void>) => Promise<void>;
  readonly rpc: (name: 'record_owner_contract', args: { readonly request: unknown }) => PromiseLike<{ data: unknown; error: unknown }>;
}): Promise<RecordOwnerContractResult> {
  let result: RecordOwnerContractResult = { ok: false, message: recordOwnerContractMessage('') };
  await input.guard('post_owner_command', async () => {
    try {
      const { data, error } = await input.rpc('record_owner_contract', {
        request: {
          orgId: input.orgId,
          sourceRef: OWNER_CONTRACT_FILE_NAME,
          version: input.hash,
          contentSha256: input.hash,
          body: input.text,
        },
      });
      if (error) {
        result = { ok: false, message: recordOwnerContractMessage('') };
        return;
      }
      const answer = data as RpcAnswer;
      if (answer?.ok !== true) {
        result = { ok: false, message: recordOwnerContractMessage(answer?.error?.code ?? '') };
        return;
      }
      const identity = parseRecordedOwnerContract(answer.value);
      if (identity === null) {
        result = { ok: false, message: 'Brunel did not return an identity for the recorded contract.' };
        return;
      }
      result = { ok: true, message: `Recorded. Hash ${identity.hash}. Recorded ${identity.recordedAt}.` };
    } catch {
      result = { ok: false, message: recordOwnerContractMessage('') };
    }
  });
  return result;
}
